/**
 * Train's pure half (`docs/plans/quevee-voice.md` §6, the approved mock):
 * what the dialog captured, the bar it is about, and the body it saves.
 *
 * `components/TrainDialog.tsx` holds everything that needs a browser — the
 * picture, the microphone — and calls in here for the rest.
 */

import type { Condition, ScreenerRowCapture, TrainBody, VoiceFile } from "../api/voice";
import type { ChartSnapshot } from "./chart-snapshot";
import { barRows, exportDate, type ChartTable } from "./chart-export";

/** Everything the dialog took off the screen when it opened. */
export interface Captured {
  symbol: string;
  interval: string;
  /** The last bar on the chart, `YYYY-MM-DD`; absent before the chart loaded. */
  barDate?: string;
  chart?: ChartSnapshot;
  screener?: ScreenerRowCapture;
  /** Bare base64 PNG. */
  image?: string;
}

/** The newest bar the chart holds — a projection past it is not a bar. */
export function lastBarDate(table: ChartTable | undefined): string | undefined {
  if (!table || table.xKind !== "seconds") return undefined;
  const rows = barRows(table);
  const last = rows[rows.length - 1];
  if (last === undefined) return undefined;
  return exportDate(table.x[last]!, "seconds").slice(0, 10);
}

/** The line under the thumbnail, one per part: ✓ and what it holds, or what is missing. */
export function capturedLines(c: Captured): { ok: boolean; label: string; detail: string }[] {
  return [
    {
      ok: !!c.chart,
      label: "Chart",
      detail: c.chart ? `${c.chart.barsShown} bars, ${c.chart.indicators.length} indicators` : "not loaded",
    },
    {
      ok: !!c.screener,
      label: "Screener row",
      detail: c.screener
        ? `${c.screener.columns.length} columns${c.screener.screen ? ` · ${c.screener.screen}` : ""}`
        : "not on the screen shown",
    },
    { ok: !!c.image, label: "Picture", detail: c.image ? "taken" : "could not be taken" },
  ];
}

/** A level box: blank is no level; anything else must be a positive number. */
export function parseLevel(text: string): number | undefined | "bad" {
  const t = text.trim().replace(/,/g, "");
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : "bad";
}

export interface TrainForm {
  read: string;
  condition?: Condition;
  pattern: string;
  stop: string;
  target: string;
  origin: "sample" | "post";
}

/** The save body, or the sentence that says why not. */
export function trainBody(
  c: Captured,
  form: TrainForm,
  recordings: VoiceFile[],
): { body: TrainBody } | { error: string } {
  const read = form.read.trim();
  if (!read) return { error: "Say or type your read first." };
  const stop = parseLevel(form.stop);
  const target = parseLevel(form.target);
  if (stop === "bad") return { error: "Stop must be a price." };
  if (target === "bad") return { error: "Target must be a price." };
  const pattern = form.pattern.trim();
  // The picture travels as a source of its own; the snapshot never carries it twice.
  const { imagePng: _picture, ...chart } = c.chart ?? ({} as ChartSnapshot);
  return {
    body: {
      origin: form.origin,
      symbol: c.symbol,
      interval: c.interval,
      ...(c.barDate ? { barDate: c.barDate } : {}),
      read,
      expected: {
        ...(form.condition ? { condition: form.condition } : {}),
        ...(pattern ? { pattern } : {}),
        ...(stop !== undefined ? { stop } : {}),
        ...(target !== undefined ? { target } : {}),
      },
      ...(c.chart ? { chart } : {}),
      ...(c.screener ? { screener: c.screener } : {}),
      ...(c.image ? { image: { contentType: "image/png", data: c.image } } : {}),
      ...(recordings.length ? { recordings } : {}),
    },
  };
}

/** A dictation appended to what is in the box, a space between. */
export function appendRead(current: string, spoken: string): string {
  const said = spoken.trim();
  if (!said) return current;
  const was = current.replace(/\s+$/, "");
  return was ? `${was} ${said}` : said;
}

/** `m:ss` for the speak key's timer. */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
